import { useCallback } from "react";
import { ChevronRight, Sparkles } from "lucide-react";
import type { FragmentLinkRole } from "@/client/db";
import { dismissPlacement, openPlacementReview, restorePlacement } from "@/client/hooks/use-placement-suggestions";
import { useCreateScene } from "@/client/hooks/use-scenes";
import type { Placement } from "@/client/hooks/use-story-ai";
import { pushUndo, type UndoEntry } from "@/client/lib/undo";
import { addedEntry, groupEntry, linkFragment } from "@/client/lib/undo-entries";

/** Undoing an accepted suggestion offers it again */
function suggestionEntry(fragmentId: string, placement: Placement): UndoEntry {
  return {
    label: "Accept a suggestion",
    run: async () => {
      restorePlacement(fragmentId, placement);
      return {
        label: "Accept a suggestion",
        run: async () => {
          dismissPlacement(fragmentId, placement);
          return suggestionEntry(fragmentId, placement);
        },
      };
    },
  };
}

/**
 * Accepts one AI placement: links the fragment there, creating the scene
 * first for a new-scene suggestion. The author can change the role and a new
 * scene's title and summary before accepting. One undo takes it all back and
 * offers the suggestion again.
 */
export function useAcceptPlacement(storyId: string) {
  const { mutateAsync: createScene } = useCreateScene();
  return useCallback(
    async (
      fragmentId: string,
      placement: Placement,
      changes: { role?: FragmentLinkRole; title?: string; summary?: string | null } = {}
    ): Promise<void> => {
      const role = changes.role ?? placement.role;
      const entries: UndoEntry[] = [suggestionEntry(fragmentId, placement)];
      let label: string;
      if (placement.kind === "newScene") {
        const title = changes.title?.trim() || placement.title;
        const scene = await createScene({
          chapterId: placement.chapterId,
          storyId,
          data: {
            title,
            summary: (changes.summary !== undefined ? changes.summary : placement.summary) || undefined,
          },
        });
        entries.push(addedEntry(storyId, "scene", scene.id, title));
        const linked = await linkFragment({ storyId, fragmentId, target: { sceneId: scene.id }, role });
        if (linked) entries.push(linked);
        label = `Add "${title}"`;
      } else {
        const target =
          placement.kind === "scene" ? { sceneId: placement.sceneId } : { chapterId: placement.chapterId };
        const linked = await linkFragment({ storyId, fragmentId, target, role });
        if (linked) entries.push(linked);
        label = linked?.label ?? "Accept a suggestion";
      }
      dismissPlacement(fragmentId, placement);
      pushUndo(groupEntry(label, entries));
    },
    [storyId, createScene]
  );
}

/** A card's line for its AI suggestions; the review dialog shows them in full. */
export function PlacementSummary({ fragmentId, placements }: { fragmentId: string; placements: Placement[] }) {
  if (placements.length === 0) return null;
  return (
    <button
      type="button"
      onClick={() => openPlacementReview(fragmentId)}
      className="mt-2 flex w-full items-center gap-1.5 rounded-md bg-primary/5 px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-primary/10"
    >
      <Sparkles className="size-3.5 shrink-0" />
      <span className="flex-1">
        {placements.length} suggested {placements.length === 1 ? "place" : "places"}
      </span>
      Review
      <ChevronRight className="size-3.5" />
    </button>
  );
}
